/**
 * @engine/vfx — Init stage generator.
 *
 * Emits the Slang module that initializes newly-spawned particles. The GPU
 * semantics are ported verbatim from the v1 WGSL generator: spawn-count gate,
 * Unity-style dead-list pop with underflow/bounds checks, event sourcing for
 * event-driven systems, base state + defaults, node calls, source-event
 * inheritance, and the emitter transform.
 */

import type { VFXGraph } from '../../VFXGraph';
import type { VFXExtractedSystem, VFXCompilerOptions } from '../types';
import { CallEmitter } from '../codegen/CallEmitter';
import { NodeFunctionCache } from '../codegen/NodeFunctionEmitter';
import { formatFloat } from '../codegen/slangTypes';
import { TWEAK_CAPACITY, type TweakAllocator } from '../codegen/Tweaks';
import {
  INIT_STAGE_BUILTINS,
  assembleStageModule,
  optionalBuiltinPrologue,
} from './common';

export interface StageResult {
  /** Full Slang module source. */
  readonly source: string;
  /** Codegen warnings (missing builtins, unresolved variables, …). */
  readonly warnings: readonly string[];
  /** Line ranges → node instances (diagnostic attribution). */
  readonly sourceMap: import('./common').VFXStageSourceMap;
}

export function generateInitStage(
  system: VFXExtractedSystem,
  graph: VFXGraph,
  options: VFXCompilerOptions,
  /** Editor tweak mode: per-system allocator shared with the update stage. */
  tweaks: TweakAllocator | null = null
): StageResult {
  const workgroupSize = options.workgroupSize ?? 64;
  const isEventDriven = system.spawn.config.mode === 'event';

  const initNodes = system.initialize.nodes;
  const hasInheritPosition = initNodes.some(n => n.type === 'vfx.init.inherit_source_position');
  const hasInheritVelocity = initNodes.some(n => n.type === 'vfx.init.inherit_source_velocity');
  const hasInheritColor = initNodes.some(n => n.type === 'vfx.init.inherit_source_color');

  // Inherit nodes are handled by the stage epilogue, not as node calls.
  const regularNodes = initNodes.filter(n => !n.type.startsWith('vfx.init.inherit_source_'));

  // Emit node calls first — we need to know which builtins they require
  // before assembling the main() prologue.
  const emitter = new CallEmitter(graph, new NodeFunctionCache(), INIT_STAGE_BUILTINS, tweaks);
  emitter.emitNodes(regularNodes);
  const emission = emitter.result();
  const used = emission.builtinsUsed;

  const main: string[] = [];

  main.push('let localIdx = id.x;');
  // All systems read spawn count from counters (set by spawn shader or event system)
  main.push('let spawnCount = counters[0].emitCount.load();');
  main.push('if (localIdx >= spawnCount) { return; }');
  main.push('');

  // Unity-style dead-list pop with underflow + bounds checks
  main.push('// Claim a free slot from the dead list');
  main.push('var deadCountOld = counters[0].deadCount.load();');
  main.push('if (deadCountOld == 0u) { return; }');
  main.push('deadCountOld = counters[0].deadCount.sub(1u);');
  main.push('if (deadCountOld == 0u)');
  main.push('{');
  main.push('    // Underflow (dead list was empty or race) — undo and bail');
  main.push('    counters[0].deadCount.add(1u);');
  main.push('    return;');
  main.push('}');
  main.push('let stackIdx = deadCountOld - 1u;');
  main.push('if (stackIdx >= uniforms.maxParticles)');
  main.push('{');
  main.push('    counters[0].deadCount.add(1u);');
  main.push('    return;');
  main.push('}');
  main.push('let idx = deadList[stackIdx];');
  main.push('var particle = particles[idx];');
  main.push('');

  // Event sourcing: real for event-driven systems, zeroed if any node reads
  // `sourceEvent` in a non-event-driven system.
  const needsSourceEvent = isEventDriven || used.has('sourceEvent');
  if (needsSourceEvent) {
    main.push('var sourceEvent : GPUEvent;');
    main.push('sourceEvent.position = float3(0.0);');
    main.push('sourceEvent.velocity = float3(0.0);');
    main.push('sourceEvent.color = float4(1.0);');
    main.push('sourceEvent.spawnCount = 0u;');
    main.push('sourceEvent.eventId = 0u;');
  }
  if (isEventDriven) {
    main.push('// Map localIdx to its source event via cumulative spawn counts');
    main.push('let evtCount = eventCount[0].count.load();');
    main.push('var cumulativeSpawn = 0u;');
    main.push('var foundEvent = false;');
    main.push('for (uint i = 0u; i < min(evtCount, 1024u); i++)');
    main.push('{');
    main.push('    let prevCumulative = cumulativeSpawn;');
    main.push('    cumulativeSpawn += events[i].spawnCount;');
    main.push('    if (localIdx >= prevCumulative && localIdx < cumulativeSpawn)');
    main.push('    {');
    main.push('        sourceEvent = events[i];');
    main.push('        foundEvent = true;');
    main.push('        break;');
    main.push('    }');
    main.push('}');
    main.push('if (!foundEvent && evtCount > 0u) { sourceEvent = events[0]; }');
    main.push('');
  }

  // Base particle state (must always be set)
  main.push('// Initialize particle state');
  main.push('let baseSeed = idx * 7u + uint(uniforms.time * 10000.0);');
  main.push('particle.alive = 1u;');
  main.push('particle.lifetime = 0.0;');
  main.push('particle.seed = baseSeed;');
  main.push('');

  // Defaults (nodes override below)
  main.push('// Defaults (initialization nodes override)');
  main.push('particle.maxLifetime = 2.0;');
  main.push('particle.position = float3(0.0);');
  main.push('particle.velocity = float3(0.0);');
  main.push('particle.color = float4(1.0);');
  main.push('particle.size = 10.0;');
  main.push('particle.scale = float2(1.0);');
  main.push('particle.rotation = 0.0;');
  main.push('particle.angularVelocity = 0.0;');
  main.push('');

  // Optional builtins requested by node functions
  const prologue = optionalBuiltinPrologue(used);
  if (prologue.length > 0) {
    main.push(...prologue);
    main.push('');
  }

  // Node calls (operator dependencies first — already ordered by the emitter)
  let emissionStartIndex = -1;
  if (emission.lines.length > 0) {
    main.push('// Initialization nodes');
    emissionStartIndex = main.length;
    main.push(...emission.lines);
    main.push('');
  }

  // Inherit from source event (event-driven systems)
  if (isEventDriven) {
    if (hasInheritPosition) {
      main.push('particle.position = sourceEvent.position;');
    }
    if (hasInheritVelocity) {
      const velocityNode = initNodes.find(n => n.type === 'vfx.init.inherit_source_velocity');
      const scale = Number(velocityNode?.inputValues?.scale ?? 1.0);
      main.push(`particle.velocity += sourceEvent.velocity * ${formatFloat(scale)};`);
    }
    if (hasInheritColor) {
      main.push('particle.color = sourceEvent.color;');
    }
  }

  // Emitter transform (skipped when position comes from a source event)
  if (!isEventDriven || !hasInheritPosition) {
    main.push('');
    main.push('// Apply emitter scale to local position, velocity, and size');
    main.push('particle.position.x *= uniforms.emitterScaleX;');
    main.push('particle.position.y *= uniforms.emitterScaleY;');
    main.push('particle.velocity.x *= uniforms.emitterScaleX;');
    main.push('particle.velocity.y *= uniforms.emitterScaleY;');
    main.push('particle.size *= (uniforms.emitterScaleX + uniforms.emitterScaleY) * 0.5;');
    main.push('');
    main.push('// Apply emitter position offset');
    main.push('particle.position.x += uniforms.emitterX;');
    main.push('particle.position.y += uniforms.emitterY;');
  }

  main.push('');
  main.push('particles[idx] = particle;');
  main.push('counters[0].aliveCount.add(1u);');

  const assembled = assembleStageModule({
    attributes: options.attributes,
    emission,
    mainLines: main,
    workgroupSize,
    emissionStartIndex,
    tweakCapacity: tweaks ? TWEAK_CAPACITY : 0,
  });

  return {
    source: assembled.source,
    sourceMap: assembled.sourceMap,
    warnings: emission.warnings,
  };
}
